<template>
    <div class="crud-area">
        <div class="container-fluid">
            <div class="crud-header d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 class="crud-title">Projectos</h2>
                    <p class="crud-subtitle">Gerir os projectos da fundação</p>
                </div>
                <button class="btn-primary-grad" @click="openCreate">
                    <i class="ri-add-line"></i> Novo Projecto
                </button>
            </div>

            <!-- Filtro categoria -->
            <div class="mb-3">
                <select v-model="filterCategoryId" class="form-input" style="max-width:220px" @change="load">
                    <option value="">Todas as categorias</option>
                    <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name_pt }}</option>
                </select>
            </div>

            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
            </div>
            <div v-if="error" class="alert-error mb-3"><i class="ri-error-warning-line"></i> {{ error }}</div>

            <div v-if="!loading" class="crud-card">
                <div class="table-responsive">
                    <table class="crud-table">
                        <thead>
                            <tr>
                                <th>Título (PT)</th>
                                <th>Categoria</th>
                                <th class="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="items.length === 0">
                                <td colspan="3" class="text-center text-muted py-4">Sem projectos registados.</td>
                            </tr>
                            <tr v-for="item in items" :key="item.id">
                                <td>{{ item.title_pt }}</td>
                                <td><span class="badge-cat">{{ item.category?.name_pt || '—' }}</span></td>
                                <td class="text-end">
                                    <button class="btn-icon edit" @click="openEdit(item)"><i
                                            class="ri-edit-line"></i></button>
                                    <button class="btn-icon delete" @click="confirmDelete(item)"><i
                                            class="ri-delete-bin-line"></i></button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="crud-pagination d-flex justify-content-between align-items-center mt-3"
                    v-if="total > limit">
                    <span class="text-muted small">{{ total }} projectos</span>
                    <div class="d-flex gap-2">
                        <button class="btn-page" :disabled="page === 1" @click="changePage(page - 1)">‹</button>
                        <span class="page-info">{{ page }} / {{ totalPages }}</span>
                        <button class="btn-page" :disabled="page >= totalPages" @click="changePage(page + 1)">›</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Criar / Editar -->
        <div v-if="showModal" class="crud-modal-overlay" @click.self="closeModal">
            <div class="crud-modal">
                <div class="crud-modal-header">
                    <h3>{{ editing ? 'Editar Projecto' : 'Novo Projecto' }}</h3>
                    <button class="btn-close-modal" @click="closeModal"><i class="ri-close-line"></i></button>
                </div>
                <form @submit.prevent="save" class="crud-modal-body">
                    <div class="row g-3">
                        <div class="col-12">
                            <label>Título (PT) *</label>
                            <input v-model="form.title_pt" class="form-input" required />
                        </div>
                        <div class="col-12">
                            <label>Título (EN)</label>
                            <input v-model="form.title_en" class="form-input" />
                        </div>
                        <div class="col-12">
                            <label>Descrição (PT) *</label>
                            <textarea v-model="form.description_pt" class="form-input" rows="3" required></textarea>
                        </div>
                        <div class="col-12">
                            <label>Descrição (EN)</label>
                            <textarea v-model="form.description_en" class="form-input" rows="3"></textarea>
                        </div>
                        <div class="col-12">
                            <label>Categoria *</label>
                            <select v-model="form.categoryId" class="form-input" required>
                                <option value="">Selecionar categoria</option>
                                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name_pt }}</option>
                            </select>
                        </div>
                        <div class="col-12">
                            <label>Imagem de Capa</label>
                            <ImageUpload :value="form.coverImageUrl" @update:value="form.coverImageUrl = $event" />
                        </div>
                        <!-- Push Notification -->
                        <div class="col-12 mt-3" v-if="!editing">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" id="sendPush" v-model="form.sendPush">
                                <label class="form-check-label ms-2" for="sendPush">
                                    Enviar Push Notification para todos os dispositivos
                                </label>
                            </div>
                        </div>
                    </div>
                    <div class="crud-modal-footer mt-4">
                        <button type="button" class="btn-secondary" @click="closeModal">Cancelar</button>
                        <button type="submit" class="btn-primary-grad" :disabled="saving">
                            <span v-if="saving"><i class="ri-loader-4-line spin"></i> A guardar...</span>
                            <span v-else>{{ editing ? 'Guardar' : 'Criar' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Confirmação de Eliminar -->
        <div v-if="showDelete" class="crud-modal-overlay" @click.self="showDelete = false">
            <div class="crud-modal crud-modal--sm">
                <div class="crud-modal-header">
                    <h3>Eliminar Projecto</h3>
                    <button class="btn-close-modal" @click="showDelete = false"><i class="ri-close-line"></i></button>
                </div>
                <div class="crud-modal-body">
                    <p>Tem a certeza que quer eliminar <strong>{{ selectedItem?.title_pt }}</strong>?</p>
                </div>
                <div class="crud-modal-footer">
                    <button class="btn-secondary" @click="showDelete = false">Cancelar</button>
                    <button class="btn-danger" @click="deleteItem" :disabled="saving">Eliminar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import projectsService from '../../services/projectsService';
import categoriesService from '../../services/categoriesService';
import ImageUpload from '../Common/ImageUpload.vue';

export default {
    name: 'ProjectsContent',
    components: { ImageUpload },
    data() {
        return {
            items: [], loading: true, saving: false, error: '',
            page: 1, limit: 10, total: 0,
            filterCategoryId: '',
            categories: [],
            showModal: false, showDelete: false,
            editing: false, selectedItem: null,
            form: { title_pt: '', title_en: '', description_pt: '', description_en: '', categoryId: '', coverImageUrl: '', sendPush: false },
        };
    },
    computed: {
        totalPages() { return Math.ceil(this.total / this.limit); },
    },
    async mounted() {
        await this.loadCategories();
        this.load();
    },
    methods: {
        async loadCategories() {
            try {
                this.categories = await categoriesService.getAll();
            } catch {
                this.error = 'Erro ao carregar categorias.';
            }
        },
        async load() {
            this.loading = true; this.error = '';
            try {
                const res = await projectsService.getAll(this.page, this.limit, this.filterCategoryId || null);
                this.items = res.data || res;
                this.total = res.total || this.items.length;
            } catch { this.error = 'Erro ao carregar projectos.'; }
            finally { this.loading = false; }
        },
        changePage(p) { this.page = p; this.load(); },
        openCreate() {
            this.editing = false;
            this.form = { title_pt: '', title_en: '', description_pt: '', description_en: '', categoryId: '', coverImageUrl: '', sendPush: false };
            this.showModal = true;
        },
        openEdit(item) {
            this.editing = true; this.selectedItem = item;
            this.form = {
                title_pt: item.title_pt,
                title_en: item.title_en || '',
                description_pt: item.description_pt,
                description_en: item.description_en || '',
                categoryId: item.categoryId || '',
                coverImageUrl: item.coverImageUrl || '',
            };
            this.showModal = true;
        },
        closeModal() { this.showModal = false; },
        async save() {
            this.saving = true;
            try {
                if (this.editing) await projectsService.update(this.selectedItem.id, this.form);
                else await projectsService.create(this.form);
                this.closeModal(); this.load();
            } catch { this.error = 'Erro ao guardar projecto.'; }
            finally { this.saving = false; }
        },
        confirmDelete(item) { this.selectedItem = item; this.showDelete = true; },
        async deleteItem() {
            this.saving = true;
            try {
                await projectsService.remove(this.selectedItem.id);
                this.showDelete = false; this.load();
            } catch { this.error = 'Erro ao eliminar projecto.'; }
            finally { this.saving = false; }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../Dashboards/GgmfDashboard/ggmf-card';

.badge-cat {
    background: linear-gradient(to right, #1765FD, #4FCB8D);
    color: #fff;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}
</style>
